<template>
  <div class="bg-gray-brand-100 min-h-screen">
    <MerchantNav />
    <main class="max-w-3xl mx-auto px-4 pb-24">
      <div v-if="loading" class="flex justify-center py-24"><BasicSpinner /></div>
      <div v-else-if="loadError" class="pt-8">
        <div class="bg-white rounded-lg border border-gray-brand-300 p-8 text-center">
          <p class="text-gray-brand-900 font-semibold mb-4">Something went wrong loading your reseller hub.</p>
          <PrimaryButton size="lg" @click="retryLoad">Retry</PrimaryButton>
        </div>
      </div>
      <template v-else>
        <header class="pt-8 pb-6">
          <h1 class="text-3xl font-bold tracking-tighter text-gray-brand-900">Reseller Hub</h1>
          <p class="text-gray-brand-500 mt-1">Manage your customers and order on their behalf.</p>
        </header>

        <section class="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          <ResellerStatTile label="Customers" :value="overview.customers_count ?? 0" />
          <ResellerStatTile label="Orders (30d)" :value="overview.orders_last_30_days ?? 0" />
          <ResellerStatTile label="Cart value (30d)" :value="formattedCartValue" />
          <ResellerStatTile label="Awaiting payment" :value="overview.awaiting_payment_count ?? 0" />
        </section>

        <section>
          <div class="flex items-center justify-between mb-4 gap-3">
            <input
              v-model="search"
              type="search"
              placeholder="Search customers"
              class="flex-1 rounded-lg border border-gray-brand-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
            <PrimaryButton size="lg" @click="addCustomerOpen = true">Add customer</PrimaryButton>
          </div>

          <p v-if="actionError" class="text-sm text-red-600 mb-3">{{ actionError }}</p>

          <p v-if="!filteredCustomers.length" class="text-gray-brand-500 py-12 text-center">
            {{
              search ? 'No customers match your search.' : 'No customers yet. Add your first customer to get started.'
            }}
          </p>

          <ul class="flex flex-col gap-3">
            <li
              v-for="row in pagedCustomers"
              :key="row.id"
              :class="[
                'bg-white rounded-lg border p-4 flex flex-wrap items-center justify-between gap-3',
                cardShadowClasses
              ]"
            >
              <div class="min-w-0">
                <p class="font-semibold text-gray-brand-900 truncate">{{ row.attributes.display_name }}</p>
                <p class="text-sm text-gray-brand-500 flex flex-wrap items-center gap-x-2">
                  <span>{{ row.attributes.phone_number }}</span>
                  <span v-if="row.attributes.last_order_date">Last order {{ row.attributes.last_order_date }}</span>
                </p>
              </div>
              <div class="flex gap-2 shrink-0">
                <SecondaryButton size="md" @click="viewOrders(row)">Orders</SecondaryButton>
                <PrimaryButton size="md" :isLoading="impersonatingId === row.id" @click="newOrder(row)">
                  New order
                </PrimaryButton>
              </div>
            </li>
          </ul>

          <div v-if="pageCount > 1" class="mt-4 flex items-center justify-between">
            <SecondaryButton size="md" class="disabled:opacity-50" :disabled="currentPage === 0" @click="prevPage">
              Previous
            </SecondaryButton>
            <span class="text-sm text-gray-brand-500">Page {{ currentPage + 1 }} of {{ pageCount }}</span>
            <SecondaryButton
              size="md"
              class="disabled:opacity-50"
              :disabled="currentPage >= pageCount - 1"
              @click="nextPage"
            >
              Next
            </SecondaryButton>
          </div>
        </section>

        <TeamMembersSection v-if="isPrimaryMember" />

        <AddCustomerModal :is-open="addCustomerOpen" @close="addCustomerOpen = false" @added="onCustomerAdded" />
        <CustomerOrdersModal :is-open="ordersOpenFor != null" :customer="ordersOpenFor" @close="ordersOpenFor = null" />
      </template>
    </main>
  </div>
</template>

<script>
import { MerchantNav } from '@/components'
import BasicSpinner from '@/components/Molecules/BasicSpinner'
import PrimaryButton from '@/components/Molecules/PrimaryButton'
import SecondaryButton from '@/components/Molecules/SecondaryButton'
import ResellerStatTile from '@/components/Reseller/ResellerStatTile'
import AddCustomerModal from '@/components/Reseller/AddCustomerModal'
import CustomerOrdersModal from '@/components/Reseller/CustomerOrdersModal'
import TeamMembersSection from '@/components/Reseller/TeamMembersSection'
import merchantFetch from '@/utils/merchantFetch'
import { cardShadowClasses } from '@/config'

export default {
  name: 'ResellerHub',
  components: {
    MerchantNav,
    BasicSpinner,
    PrimaryButton,
    SecondaryButton,
    ResellerStatTile,
    AddCustomerModal,
    CustomerOrdersModal,
    TeamMembersSection
  },
  data() {
    return {
      cardShadowClasses,
      loading: true,
      loadError: false,
      overview: {},
      customers: [],
      search: '',
      page: 0,
      pageSize: 10,
      addCustomerOpen: false,
      ordersOpenFor: null,
      impersonatingId: null,
      actionError: null
    }
  },
  computed: {
    // Only the reseller's primary user manages the team; other members never
    // see the section (the API 403s them regardless).
    isPrimaryMember() {
      return this.$store.getters.reseller?.is_primary === true
    },
    formattedCartValue() {
      const amount = this.overview.cart_value_last_30_days ?? 0
      return '$' + Number(amount).toFixed(2)
    },
    filteredCustomers() {
      const query = this.search.trim().toLowerCase()
      if (!query) return this.customers
      return this.customers.filter((row) => {
        const name = row.attributes.display_name || ''
        const phone = row.attributes.phone_number || ''
        return name.toLowerCase().includes(query) || phone.includes(query)
      })
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.filteredCustomers.length / this.pageSize))
    },
    // Clamped so a shrinking result set (e.g. narrowing a search) never strands
    // the view on a page past the end.
    currentPage() {
      return Math.min(this.page, this.pageCount - 1)
    },
    pagedCustomers() {
      const start = this.currentPage * this.pageSize
      return this.filteredCustomers.slice(start, start + this.pageSize)
    }
  },
  watch: {
    search() {
      this.page = 0
    }
  },
  async mounted() {
    window.scrollTo(0, 0)
    if (!this.$store.getters.isLoggedIn) {
      this.$router.replace({ name: 'NakedLogin' })
      return
    }
    await this.$store.dispatch('getCustomerInfo', undefined)
    if (!this.$store.getters.reseller?.is_active) {
      this.$router.replace('/')
      return
    }
    await this.attemptLoad()
  },
  methods: {
    async loadData() {
      const [overviewRes, customersRes] = await Promise.all([
        merchantFetch('reseller/overview', {}),
        merchantFetch('reseller/customers', {})
      ])
      this.overview = overviewRes.data || {}
      this.customers = customersRes.data || []
    },
    async attemptLoad() {
      this.loadError = false
      try {
        await this.loadData()
      } catch (e) {
        this.loadError = true
      } finally {
        this.loading = false
      }
    },
    async retryLoad() {
      this.loading = true
      await this.attemptLoad()
    },
    async onCustomerAdded() {
      this.addCustomerOpen = false
      this.page = 0
      await this.loadData()
    },
    prevPage() {
      this.page = Math.max(0, this.currentPage - 1)
    },
    nextPage() {
      this.page = Math.min(this.pageCount - 1, this.currentPage + 1)
    },
    viewOrders(row) {
      this.ordersOpenFor = row
    },
    async newOrder(row) {
      this.actionError = null
      this.impersonatingId = row.id
      try {
        const res = await merchantFetch('reseller/impersonation_tokens', {
          method: 'POST',
          body: JSON.stringify({ conversation_user_pair_id: row.id })
        })
        this.$store.dispatch('startResellerImpersonation', { token: res.token })
        this.$router.push('/')
      } catch (e) {
        this.actionError = 'Could not start an order for this customer. Please try again.'
      } finally {
        this.impersonatingId = null
      }
    }
  }
}
</script>
